import { useMediaQuery } from '@mui/material';
import { useMemo, useState } from 'react';
import { IResponse, ISortDirection, IWarrantyMessage, IDateRange } from '../../../models';
import { useQuery } from 'react-query';
import { MobileTable, Pagination, Table } from '../../../components';
import { WarrantyMessagesFilters } from './warranty-messages-filters';
import { getWarrantyMessages } from '../../../fetch';
import { formatShortFriendlyDateWithTime } from '../../../helpers';

interface IWarrantyMessagesListProps {
  onFiltersChange?: (filters: {
    searchText?: string;
    startDate?: string;
    endDate?: string;
    jobStatus?: string;
  }) => void;
}

export const WarrantyMessagesList = ({ onFiltersChange }: IWarrantyMessagesListProps) => {
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [filters, setFilters] = useState<{
    searchText?: string;
    startDate?: string;
    endDate?: string;
    jobStatus?: string;
  } | null>({});

  const [searchValue, setSearchValue] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [selectedJobStatus, setSelectedJobStatus] = useState<string>('');
  const [selectedDateRange, setSelectedDateRange] = useState<IDateRange>({
    startDate: null,
    endDate: null,
    key: 'selection',
    inputValue: ''
  });
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);

  const [selectedSort, setSelectedSort] = useState<string>('NeighborhoodName');
  const [sortDirection, setSortDirection] = useState<{
    NeighborhoodName?: ISortDirection;
    LotNumber?: ISortDirection;
    HomeownerName?: ISortDirection;
    JobStatus?: ISortDirection;
    SubmissionDate?: ISortDirection;
  }>({
    NeighborhoodName: 'Asc'
  });

  const {
    isLoading,
    isFetching,
    data: warrantyMessagesData
  } = useQuery<IResponse<IWarrantyMessage[]>, Error>(
    ['getWarrantyMessages', page, perPage, selectedSort, sortDirection, filters],
    () =>
      getWarrantyMessages({
        page: page + 1,
        perPage: perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        ...filters
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const handleSearch = (searchText: string) => {
    setSearchedValue(searchText);
    setHasAppliedFilters(true);
    setPage(0);
    const newFilters = {
      ...filters,
      searchText: searchText || undefined
    };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setSearchedValue('');
    const newFilters = {
      ...filters,
      searchText: undefined
    };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const data = useMemo(() => warrantyMessagesData?.records ?? [], [warrantyMessagesData]);
  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      setSelectedSort(column);
      setSortDirection({
        ...sortDirection,
        [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
      });
    };
    return [
      {
        Header: 'Neighborhood',
        accessor: 'displayName',
        isServerSorted: selectedSort === 'NeighborhoodName',
        isServerSortedDesc: sortDirection.NeighborhoodName === 'Desc',
        handleClickColumn: () => handleClickColumn('NeighborhoodName')
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        isServerSorted: selectedSort === 'LotNumber',
        isServerSortedDesc: sortDirection.LotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('LotNumber')
      },
      {
        Header: 'Homeowner Name',
        accessor: 'clientName',
        isServerSorted: selectedSort === 'HomeownerName',
        isServerSortedDesc: sortDirection.HomeownerName === 'Desc',
        handleClickColumn: () => handleClickColumn('HomeownerName')
      },
      {
        Header: 'Job Status',
        accessor: 'jobStatus',
        isServerSorted: selectedSort === 'JobStatus',
        isServerSortedDesc: sortDirection.JobStatus === 'Desc',
        handleClickColumn: () => handleClickColumn('JobStatus')
      },
      {
        Header: 'Submission Date',
        accessor: 'submissionDate',
        isServerSorted: selectedSort === 'SubmissionDate',
        isServerSortedDesc: sortDirection.SubmissionDate === 'Desc',
        handleClickColumn: () => handleClickColumn('SubmissionDate'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IWarrantyMessage } };
        }) => {
          return <span>{formatShortFriendlyDateWithTime(original.submissionDate)}</span>;
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, selectedSort]);

  return (
    <>
      <WarrantyMessagesFilters
        isLoading={isLoading || isFetching}
        applyFilters={(clearFilters?: boolean) => {
          if (clearFilters) {
            const clearedFilters = {
              searchText: undefined,
              startDate: undefined,
              endDate: undefined,
              jobStatus: undefined
            };
            setFilters(clearedFilters);
            onFiltersChange?.(clearedFilters);
          } else {
            setPage(0);
            const newFilters = {
              ...filters,
              searchText: searchedValue || undefined,
              startDate: selectedDateRange?.startDate?.toISOString() || undefined,
              endDate: selectedDateRange?.endDate?.toISOString() || undefined,
              jobStatus: selectedJobStatus || undefined
            };
            setFilters(newFilters);
            onFiltersChange?.(newFilters);
          }
        }}
        setHasAppliedFilters={setHasAppliedFilters}
        hasAppliedFilters={hasAppliedFilters}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        setSearchedValue={setSearchedValue}
        handleSearch={handleSearch}
        handleClearSearch={handleClearSearch}
        searchPlaceholder="Search..."
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={setSelectedDateRange}
        selectedJobStatus={selectedJobStatus}
        setSelectedJobStatus={setSelectedJobStatus}
      />
      <Table
        columns={columns}
        data={data}
        isLoading={isLoading || isFetching}
        hidePagination
        mobileProps={{
          fields: columns
            .filter(c => c.Header)
            .map(c => ({
              name: c.Header,
              accessor: c.accessor,
              Cell: c?.Cell
            })),
          getAccordionHeader: (item: IWarrantyMessage) =>
            `${item.displayName} Lot ${item.lotNumber}`
        }}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
      />
      {!isLoading && !isFetching && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </>
  );
};
